// @/Screens/Books/components/CancelSessionModal.tsx
import React, {useState, useEffect, useMemo} from 'react';
import {View, StyleSheet, TextInput, ActivityIndicator} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton/CCButton';
import CCLoader from '@/Atoms/CCLoader/CCLoader';
import CCColors from '@/Utils/CCColors';
import {FontSizes, WINDOW_WIDTH} from '@/Utils/Dimensions';
import SlideModal from '@/Atoms/SlideModal';
import useCheckCancellationLimitApi from '@/Hooks/WebinarApi/useCheckCancellationLimitApi';
import Box from '@/Atoms/Box';
import {MaterialCommunityIcons} from '@expo/vector-icons';

interface CancelSessionModalProps {
  visible: boolean;
  sessionData: {
    id: number;
    topic: string;
    date: string;
    time: string;
    cancellation_type: 'group' | 'personal';
    batch_code?: string;
    user_role?: 'coach' | 'student' | 'parent';
    class_type?: string;
  } | null;
  onClose: () => void;
  onConfirm: (sessionId: number, reason: string) => void;
  isLoading?: boolean;
  userRole?: string;
}

const cancellationStatuses = {
  withinLimit: {
    heading: 'Within Limit',
    icon: 'alert-outline',
    message: allowed =>
      `You can cancel this class. Max ${allowed} cancellations allowed — use them wisely!`,

    backgroundColor: CCColors.Cancel.Ltyellow,
    borderColor: CCColors.Cancel.BorderYellow,
    iconColor: CCColors.Red,
  },
  lastCancellation: {
    heading: 'Last Cancellation',
    icon: 'bell-alert-outline',
    message:
      'This is your last allowed cancellation. Cancel only if really needed.',
    backgroundColor: CCColors.Cancel.Ltyellow,
    borderColor: CCColors.Cancel.BorderYellow,
    iconColor: CCColors.Red,
  },

  limitExceeded: {
    heading: 'Limit Exceeded',
    icon: 'diameter-variant',
    message: {
      student:
        "You have already reached your cancellation limit for this month. If you cancel this class now, it will be marked as 'Held' as a penalty — you will not get this class or topic back. This only affects this one class; your future classes are not affected.",
      coach:
        'Cancellation limit crossed — you will be penalized for cancellation. Try to attend.',
    },

    backgroundColor: CCColors.Cancel.Pink,
    borderColor: CCColors.Cancel.Red,
    iconColor: CCColors.WrongMoves.Background,
  },
  tooLate: {
    heading: 'Too Late',
    icon: 'alarm',
    message: {
      student:
        'Cancellation window closed (within 15 mins). Class will be counted — please attend.',
      coach:
        'Cancellation window closed (within 4 hrs). You will be penalized for cancellation — please attend.',
    },

    backgroundColor: CCColors.Cancel.Pink,
    borderColor: CCColors.Cancel.Red,
    iconColor: CCColors.WrongMoves.Background,
  },
};

function getCancellationStatus(limitData, userRole) {
  const remaining = limitData?.data?.remaining_cancellations ?? 0;
  const willBreach = limitData?.data?.will_breach ?? false;
  const allowed = limitData?.data?.allowed_cancellations ?? 0;

  let status;

  if (willBreach && remaining != 0) {
    status = cancellationStatuses.tooLate;
  } else if (remaining <= 0) {
    status = cancellationStatuses.limitExceeded;
  } else if (remaining == 1) {
    status = cancellationStatuses.lastCancellation;
  } else {
    status = cancellationStatuses.withinLimit;
  }
  // Handle message resolution by type
  let message;
  if (
    status === cancellationStatuses.limitExceeded &&
    (userRole === 'student' || userRole === 'parent') &&
    limitData?.data?.warning_message
  ) {
    // Prefer the backend's authoritative breach copy (explains the class will be
    // marked Held, the topic is lost, future classes are unaffected) over the
    // local generic fallback text below.
    message = limitData.data.warning_message;
  } else if (typeof status?.message === 'function') {
    message = status?.message(allowed);
  } else if (typeof status?.message === 'object') {
    message = status?.message?.[userRole] || status?.message?.student; // fallback
  } else {
    message = status?.message;
  }
  return {
    ...status,
    message,
  };
}

export const CancelSessionModal: React.FC<CancelSessionModalProps> = ({
  visible,
  sessionData,
  onClose,
  onConfirm,
  isLoading = false,
  userRole,
}) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [showError, setShowError] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const isPTM = sessionData?.class_type === "ptm";

  // Fetch cancellation limit data
  const {
    data: limitData,
    isLoading: rawLimitLoading,
    error: limitError,
  } = useCheckCancellationLimitApi(
    sessionData?.batch_code || null,
    sessionData?.user_role || 'student',
    visible && !!sessionData?.batch_code && !isPTM,
  );

  const limitLoading = isPTM ? false : rawLimitLoading;

  const cancellationStatus = useMemo(
    () =>
      limitData?.success && limitData?.data
        ? getCancellationStatus(limitData, userRole)
        : null,
    [limitData, userRole],
  );

  const isConfirmTextValid = confirmText.toLowerCase() === 'cancel';

  const handleCancelClick = () => {
    // First step: validate reason and show final confirmation
    if (!reason.trim()) {
      setShowError(true);
      return;
    }
    setShowFinalConfirm(true);
  };

  const handleFinalConfirm = () => {
    // Second step: validate typed "cancel" and proceed
    if (!isConfirmTextValid) {
      setShowConfirmError(true);
      return;
    }

    if (sessionData) {
      onConfirm(sessionData.id, reason);
      setReason(''); // Reset reason after confirm
      setConfirmText(''); // Reset confirm text
      setShowError(false);
      setShowConfirmError(false);
      setShowFinalConfirm(false);
    }
  };

  const handleClose = () => {
    setReason(''); // Reset reason on close
    setConfirmText(''); // Reset confirm text
    setShowError(false);
    setShowConfirmError(false);
    setShowFinalConfirm(false);
    onClose();
  };

  const handleReasonChange = (text: string) => {
    setReason(text);
    if (showError && text.trim()) {
      setShowError(false);
    }
    if (text.trim() === '') {
      setShowError(true);
    }
  };

  const handleConfirmTextChange = (text: string) => {
    setConfirmText(text);
    if (showConfirmError && text.toLowerCase() === 'cancel') {
      setShowConfirmError(false);
    }
  };

  const handleBackToReason = () => {
    setShowFinalConfirm(false);
    setConfirmText('');
    setShowConfirmError(false);
  };

  return (
    <SlideModal
      visible={visible}
      modalHeaderTitle="Cancel Session"
      contentPadding={0}
      type="center"
      showHeader={false}
      customStyle={styles.modalContent}>
      <>
        {/* Loader Overlay */}
        {isLoading ? (
          <View style={styles.loaderOverlay}>
            <CCLoader loading isComponentLoader />
          </View>
        ) : null}

        <View style={styles.modalBody}>
          {sessionData ? (
            <Box
              flexDirection="column"
              style={[
                styles.sessionInfo,
                {
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                },
              ]}>
              <View style={[styles.sessionInfo, styles.shadow]}>
                <Box style={styles.new}>
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors.TextColor.GreyText}
                    fontWeight="medium">
                    Session Summary
                  </CCText>
                </Box>

                <View style={styles.infoRow}>
                  <Icon name="event" size={15} color={CCColors.Grey.Grey} />
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors.Primary.Dark}
                    style={{marginLeft: 6}}>
                    {sessionData.date}
                  </CCText>
                </View>
                <View style={styles.infoRow}>
                  <Icon
                    name="access-time"
                    size={15}
                    color={CCColors.Grey.Grey}
                  />
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors.Primary.Dark}
                    style={{marginLeft: 6}}>
                    {sessionData.time}
                  </CCText>
                </View>
                <View style={[styles.infoRow, {marginBottom: 6}]}>
                  <MaterialCommunityIcons
                    name={
                      sessionData.cancellation_type === 'group'
                        ? 'account-multiple'
                        : 'account'
                    }
                    size={15}
                    color={CCColors.Grey.Grey}
                  />
                  <CCText
                    style={{marginLeft: 6}}
                    fontSize={FontSizes[14]}
                    color={CCColors.Primary.Dark}>
                    {sessionData.class_type == 'ptm' ? 'Parent Teacher Meet' : sessionData.cancellation_type === 'group'
                      ? 'Group Session'
                      : 'Personal Session'}
                  </CCText>
                </View>
              </View>
            </Box>
          ) : null}

          <View style={styles.buttonContainer}>
            <Box flexDirection="column" style={{width: '90%'}}>
              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center">
                <CCText
                  style={{
                    marginTop: 32,
                  }}
                  fontWeight="medium"
                  color={CCColors.TextColor.Dark}
                  fontSize={FontSizes[20]}>
                  Cancel Chess Class
                </CCText>
                <Box onPress={handleClose} style={styles.bg}>
                  <MaterialCommunityIcons
                    name="close"
                    size={18}
                    color={CCColors.Primary.Dark}
                  />
                </Box>
              </Box>
              <CCText
                style={{
                  marginTop: 2,
                }}
                fontSize={FontSizes[14]}>
                Are you sure you want to cancel your class.
              </CCText>
            </Box>

            {/* Cancellation Limit Info */}
            {limitLoading ? (
              <View style={styles.limitInfoBox}>
                <ActivityIndicator size="small" color={CCColors.Primary.Dark} />
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors.Grey.Grey}
                  style={{marginLeft: 8}}>
                  Checking cancellation limits...
                </CCText>
              </View>
            ) : cancellationStatus ? (
              <View
                style={[
                  styles.limitInfoBox,
                  {
                    backgroundColor: cancellationStatus?.backgroundColor,
                    borderColor: cancellationStatus?.borderColor,
                  },
                  {padding: 8},
                ]}>
                <View style={styles.con}>
                  <Box style={styles.icons}>
                    <MaterialCommunityIcons
                      name={cancellationStatus?.icon}
                      size={20}
                      color={cancellationStatus?.iconColor}
                    />
                  </Box>
                  <CCText
                    style={{marginLeft: 5}}
                    fontSize={FontSizes[12]}
                    color={CCColors.Black}>
                    {cancellationStatus?.message}
                  </CCText>
                  <View style={styles.limitStats}></View>
                </View>
              </View>
            ) : null}

            <Box style={{width: '90%'}}>
              <TextInput
                style={[styles.textInput, showError && styles.textInputError]}
                placeholder="Reason for cancellation (required)"
                placeholderTextColor={CCColors.Grey.Grey}
                value={reason}
                onChangeText={handleReasonChange}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
              <Box
                marginTop={16}
                marginBottom={
                  isConfirmTextValid || (confirmText && !isConfirmTextValid)
                    ? 35
                    : 50
                }
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start">
                <Box
                  style={{
                    minWidth: '50%',
                  }}>
                  <TextInput
                    style={[
                      styles.confirmInput,
                      showConfirmError && styles.textInputError,
                    ]}
                    placeholder='Type "cancel"'
                    placeholderTextColor={CCColors.Grey.Grey}
                    value={confirmText}
                    onChangeText={handleConfirmTextChange}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  {confirmText && !isConfirmTextValid && (
                    <CCText
                      fontSize={FontSizes[11]}
                      color={CCColors.Orange}
                      style={{marginTop: 4}}>
                      Text doesn't match "cancel"
                    </CCText>
                  )}
                  {isConfirmTextValid && (
                    <View style={styles.validCheckmark}>
                      <Icon
                        name="check-circle"
                        size={16}
                        color={CCColors.Green}
                      />
                      <CCText
                        fontSize={FontSizes[11]}
                        color={CCColors.Green}
                        style={{marginLeft: 4}}>
                        Matched
                      </CCText>
                    </View>
                  )}
                </Box>
                <Box>
                  <CCButton
                    contentContainerStyle={styles.content}
                    style={[
                      styles.cancelButton,
                      (!isConfirmTextValid ||
                        isLoading ||
                        reason.trim() === '') &&
                        styles.disabledButton,
                    ]}
                    hoverEffect={false}
                    onPress={handleFinalConfirm}
                    disabled={
                      !isConfirmTextValid || isLoading || reason.trim() === ''
                    }>
                    <CCText
                      color={CCColors.Primary.Brown}
                      fontWeight="medium"
                      fontSize={FontSizes[12]}>
                      {isLoading ? 'Cancelling...' : 'Cancel Class'}
                    </CCText>
                  </CCButton>
                </Box>
              </Box>
            </Box>
          </View>
        </View>
      </>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: 400,
  },
  modalBody: {
    flexDirection: 'row',
  },
  sessionInfo: {
    backgroundColor: CCColors.CCTableBg,
    padding: 32,
    borderRadius: 8,
    borderLeftWidth: 0,
    borderLeftColor: CCColors.Red,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    marginHorizontal: 15,
  },
  typeTag: {
    marginTop: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 12,
    alignItems: 'center',
    marginLeft: 6,
    marginHorizontal: 15,
  },
  limitInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    borderWidth: 1,
    width: '90%',
  },
  limitNormalBox: {
    backgroundColor: '#E3F2FD',
    borderColor: '#90CAF9',
  },
  limitWarningBox: {
    backgroundColor: CCColors.Cancel.Ltyellow,
    borderColor: CCColors.Cancel.BorderYellow,
  },
  limitBreachBox: {
    backgroundColor: CCColors.Cancel.Pink,
    borderColor: CCColors.Cancel.Red,
  },
  limitStats: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 12,
  },
  statItem: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 4,
  },
  textInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 6,
    padding: 10,
    fontSize: FontSizes[12],
    color: CCColors.Black,
    height: 80,
    backgroundColor: '#FAFAFA',
    marginTop: 24,
  },
  textInputError: {
    borderColor: CCColors.Red,
    borderWidth: 1.5,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.Cancel.Ltyellow,
    padding: 10,
    borderRadius: 6,
    marginTop: 12,
  },
  finalConfirmSection: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#FFF5F5',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: CCColors.Red,
  },
  confirmInput: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 6,
    padding: 8,
    fontSize: FontSizes[12],
    color: CCColors.Black,
    backgroundColor: '#FFF',
  },
  validCheckmark: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  },
  actionButton: {
    flex: 1,
    // paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelButton: {
    backgroundColor: CCColors.Primary.Default,
  },
  disabledButton: {
    backgroundColor: CCColors.Primary.Default,
    opacity: 0.6,
  },
  secondaryButton: {
    backgroundColor: CCColors.Red,
  },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    zIndex: 1000,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shadow: {
    backgroundColor: CCColors.White,
    padding: 4,
    // 💡 Add shadow here
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 4, // Android shadow
    borderRadius: 8,
  },
  new: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.CCTableBg,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  bg: {
    width: 30,
    height: 30,
    borderRadius: 20,
    backgroundColor: CCColors.CCTableBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
  },
  space: {
    flexDirection: 'row',
    width: '90%',
    justifyContent: 'space-between',
    marginBottom: 20,
    marginTop: 6,
  },
  btn: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 28,
  },
  newmargin: {
    flexDirection: 'row',
    width: '90%',
    justifyContent: 'space-between',
    marginBottom: 20,
    marginTop: 6,
  },
  icons: {marginBottom: 7, marginRight: 2, marginLeft: 4},
  content: {
    paddingHorizontal: 30,
    paddingVertical: 8,
  },
  con: {flex: 1, flexDirection: 'row', alignItems: 'center'},
});
